'use client';

import type { StaticImport } from 'next/dist/shared/lib/get-img-props';
import type { ImageProps as NextImageProps } from 'next/image';

import { useEffect, useState } from 'react';

import Imgix from 'react-imgix';

import { useSiteSettingsContext } from '@/core/providers/site-settings-provider';
import { useWindowSize } from '@/shared/hooks/useWindowSize';

import { type IFocalPoint } from './get-media-props';

// Type where either Width or Height can be undefined, but not both at the same time
type ISizes = { width: number; height?: number } | { width?: number; height: number };

type Breakpoint = 'mobile' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'max';

export type IImgixSizes = Partial<Record<Breakpoint, ISizes>>;

// export interface IImgixSizes {
//     mobile?: ISizes;
//     sm?: ISizes;
//     md?: ISizes;
//     lg?: ISizes;
//     xl?: ISizes;
//     '2xl'?: ISizes;
//     max?: ISizes;
// }

export interface ImageProps extends NextImageProps {
    url?: string | StaticImport;
    imageId?: string;
    alt: string;
    focalpoint?: IFocalPoint;
    extension?: string;
    width?: number;
    height?: number;
    quality?: number;
    imgixSizes?: IImgixSizes;
    sizes?: string;
}

const breakpoints: { min: number; keys: Breakpoint[] }[] = [
    { min: 1920, keys: ['max'] },
    { min: 1440, keys: ['2xl', 'max'] },
    { min: 1280, keys: ['xl', '2xl', 'max'] },
    { min: 1024, keys: ['lg', 'xl', '2xl', 'max'] },
    { min: 768, keys: ['md', 'lg', 'xl', '2xl', 'max'] },
    { min: 640, keys: ['sm', 'md', 'lg', 'xl', '2xl', 'max'] },
    { min: 0, keys: ['mobile', 'sm', 'md', 'lg', 'xl', '2xl', 'max'] },
];

const breakpointSizes = breakpoints.reduce(
    (acc, bp) => {
        const bpKey = bp.keys[0];
        if (bpKey) {
            acc[bpKey] = bp.min;
        }
        return acc;
    },
    {} as Record<Breakpoint, number>,
);

function getMaxBreakpointImgixSize(imgixSizes?: IImgixSizes): ISizes | undefined {
    if (!imgixSizes) {
        return undefined;
    }

    const sizesSorted = Object.entries(imgixSizes)
        .sort(([a], [b]) => {
            const minA = breakpointSizes[a as Breakpoint];
            const minB = breakpointSizes[b as Breakpoint];
            return (minA || 0) - (minB || 0);
        })
        .map(([, size]) => size);

    return sizesSorted[0];
}

function getHighQualityImgixSize(
    windowWidth: number,
    imgixSizes: NonNullable<ImageProps['imgixSizes']>,
    fallback: ISizes,
): ISizes {
    const bp = breakpoints.find(b => windowWidth >= b.min);

    if (bp) {
        const key = bp.keys.find(k => imgixSizes[k]);
        if (key) {
            return imgixSizes[key]!;
        }
    }

    return fallback;
}

/**
 * Responsive image component using Imgix with focal point and dynamic sizing support.
 *
 * This component wraps the `react-imgix` component and provides:
 * - Automatic src building based on `imageId` and `extension`
 * - Support for custom aspect ratios via `width` and `height`
 * - Responsive resizing using `imgixSizes` based on Tailwind-style breakpoints
 * - Focal point support via `fp-x`, `fp-y`, and `fp-z`
 *
 * The `sizes` attribute is used for responsive image behavior. For example, `(min-width: 1024px) 40vw, 90vw`
 * means that on screens wider than 1024px, the image will take up 40% of the viewport width (vw),
 * and for smaller screens, it will take up 90% of the viewport width.
 *
 * @component
 * @example
 * <ImageImgix
 *   imageId="example-image"
 *   extension="jpg"
 *   alt="Example image"
 *   focalpoint={{ x: 0.3, y: 0.7 }}
 *   sizes="(min-width: 1024px) 40vw, 90vw"
 *   imgixSizes={{
 *     mobile: { width: 400, height: 300 },
 *     md: { width: 800, height: 600 },
 *     xl: { width: 1200, height: 900 },
 *     max: { width: 1400, height: 1024 },
 *   }}
 * />
 *
 * @param {ImageProps} props - Props extending Next.js `ImageProps`, plus:
 * @param {string} [props.imageId] - Image filename without extension, used to build the Imgix URL
 * @param {string} [props.extension] - File extension (e.g., 'jpg', 'png') to build the URL
 * @param {string} props.alt - Alt text for the image
 * @param {number} [props.width=500] - Default width used as fallback, original image size.
 * @param {number} [props.height=500] - Default height used as fallback, original image size.
 * @param {string} [props.src] - Fallback source URL, used when `imageId` is not provided
 * @param {IFocalPoint} [props.focalpoint] - Object with x and y values (0 to 1) to define image crop focus
 * @param {IImgixSizes} [props.imgixSizes] - Breakpoint-based sizes to render different resolutions
 * @param {string} [sizes] - The `sizes` attribute for the `<img>` tag, allowing responsive image scaling.

 *
 * @returns {JSX.Element} Responsive optimized image
 */

export function ImageImgix({
    imageId,
    alt,
    width = 500,
    height = 500,
    extension,
    focalpoint,
    src,
    imgixSizes,
    sizes,
    className,
    ...props
}: ImageProps) {
    const windowSize = useWindowSize();
    const windowWidth = windowSize.width;
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
    }, []);

    let imageWidth: number | undefined = width;
    let imageHeight: number | undefined = height;

    const maxSize = getMaxBreakpointImgixSize(imgixSizes);

    if (maxSize) {
        imageWidth = maxSize.width;
        imageHeight = maxSize.height;
    }

    if (mounted && imgixSizes && windowWidth) {
        const size = getHighQualityImgixSize(windowWidth, imgixSizes, { width, height });
        imageWidth = size.width;
        imageHeight = size.height;
    }

    const { imgixUrl: contextImgixUrl } = useSiteSettingsContext();
    const baseImgixUrl = contextImgixUrl || process.env.NEXT_PUBLIC_IMGIX_URL || '';
    if (!baseImgixUrl && imageId) {
        console.error('ImageImgix: No imgixUrl found in context or environment variables', {
            contextImgixUrl,
            envImgixUrl: process.env.NEXT_PUBLIC_IMGIX_URL,
            imageId,
            extension,
        });
        return null;
    }

    const imageUrl = imageId ? `${baseImgixUrl}/${imageId}.${extension}` : src ? src.toString() : null;

    if (!imageUrl) {
        return null;
    }

    return (
        <Imgix
            className={className}
            src={imageUrl}
            sizes={sizes}
            width={imageWidth}
            height={imageHeight}
            {...props}
            htmlAttributes={{
                alt: alt || '',
                loading: props.loading || 'lazy',
            }}
            imgixParams={{
                w: imageWidth,
                h: imageHeight,
                fit: 'crop',
                'fp-x': focalpoint?.x ?? 0.5,
                'fp-y': focalpoint?.y ?? 0.5,
                'fp-z': 1,
            }}
        />
    );
}
